import { OutlinedInput, Stack } from "@mui/material";
import type { HTMLAttributes } from "react";
import {
  ChangeEvent,
  useCallback,
  useState,
  FocusEvent,
  useEffect,
  KeyboardEvent
} from "react";
import { HeaderRule } from "src/components/GoTableWithSelect/interface";
import { trimZeros } from "src/utils/genericFunctions";

export const SplitCell = ({
  rowData,
  editMode,
  headerItem,
  onBlur,
  blockedSymbols = ["e", "E", "+", "-", "."],
  disableAllInputs
}: {
  rowData: any;
  editMode: boolean;
  headerItem: HeaderRule;
  onBlur?: (item: string, id: string | number, value: any) => any;
  blockedSymbols?: string[];
  disableAllInputs?: boolean;
}) => {
  const [values, setValues] = useState<any>({});

  useEffect(() => {
    const __values = headerItem?.columns?.reduce((result: any, cellData) => {
      result[cellData.id] = rowData[cellData.id];
      return result;
    }, {});
    setValues(__values);
  }, []);

  const handleInputBlur = useCallback(
    (type: string) => (event: FocusEvent<HTMLInputElement>) => {
      onBlur(headerItem?.id, rowData, values);
    },
    [headerItem?.id, onBlur, rowData, values]
  );

  const handleInputChange = useCallback(
    /* istanbul ignore next */
    (type: string, limit?: string, limitCheck = "") =>
      (event: ChangeEvent<HTMLInputElement>) => {
        let value = trimZeros(event.target.value);
        if (value.split(".")[1]?.length > 2) return;
        if (value === ".") value = "0.";
        if (value.includes(".") && value.split(".")[0] === "") {
          value = "0" + value;
        }

        let limitValue = limit || Number.MAX_SAFE_INTEGER;
        if (rowData[limitCheck]) limitValue = Number.MAX_SAFE_INTEGER;
        if ((+value >= 0 && +value <= +limitValue) || value === ".") {
          const __values = headerItem?.columns?.reduce(
            (result: any, cellData) => {
              result[cellData.id] = rowData[cellData.id];
              return result;
            },
            {}
          );

          setValues({ ...__values, [type]: value });
          onBlur(headerItem?.id, rowData, { ...__values, [type]: value });
        } else {
          event.preventDefault();
        }
      },
    [headerItem?.columns, headerItem?.id, onBlur, rowData]
  );

  const blockOnKeyPress = useCallback(
    (cellData: any) => (e: KeyboardEvent<HTMLInputElement>) => {
      blockedSymbols.includes(e.key) && e.preventDefault();
      let limit = rowData[cellData?.limit] || Number.MAX_SAFE_INTEGER;
      if (rowData[cellData?.limitCheck]) limit = Number.MAX_SAFE_INTEGER;
      Number(e.key) > +limit && e.preventDefault();
    },
    [headerItem?.columns, headerItem?.id, rowData]
  );
  const onFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => event.target.select(),
    []
  );

  return (
    <Stack sx={{ flexDirection: "row", gap: "40px", height: "34px" }}>
      {!editMode
        ? headerItem?.columns?.map((cellData) => (
            <Stack
              key={cellData.id}
              sx={{
                height: "36px",
                fontSize: "14px",
                textAlign: "right",
                justifyContent: "center",
                ...cellData.sx
              }}
            >
              {rowData[cellData.id]}
            </Stack>
          ))
        : headerItem?.columns?.map((cellData) => {
            return (
              <OutlinedInput
                key={cellData?.id}
                value={rowData[cellData?.id]}
                onChange={handleInputChange(
                  cellData?.id,
                  rowData[cellData?.limit],
                  cellData?.limitCheck
                )}
                onBlur={handleInputBlur(cellData?.id)}
                onFocus={onFocus}
                sx={{ height: "36px", fontSize: "14px" }}
                slotProps={{
                  input: {
                    id: "split-input-id" + cellData?.id,
                    style: { textAlign: "right", padding: "9px" },
                    "aria-label": "input",
                    "data-testid": "input-split-input" + cellData?.id
                  } as HTMLAttributes<HTMLInputElement>
                }}
                onKeyDown={blockOnKeyPress(cellData)}
                disabled={
                  rowData?.inputDisabled?.[cellData.id] || disableAllInputs
                }
              />
            );
          })}
    </Stack>
  );
};
